/**
 * Shadow Reading Database
 * 独立的数据库实例，用于存储影子跟读相关数据
 *
 * 设计决策：
 * - 使用独立数据库而非扩展 indexedDb.ts，因为视频 Blob 较大且数据结构与卡片完全不同
 * - 分离 materials 和 blobs 表，便于独立管理大文件
 * - 支持 AI 对话会话的持久化
 */
import Dexie, { Table } from 'dexie';

// ============= 类型定义 =============

/** 字幕片段 */
export interface SubtitleSegment {
  index: number;
  startMs: number;
  endMs: number;
  text: string;
  translation?: string;
}

/** 影子跟读进度 */
export interface ShadowProgress {
  practicedSentences: number[];
  fluencyScores: Record<number, number>;
  overallFluency?: number;
  lastPracticedAt?: number;
}

/** Retelling 分析结果 */
export interface RetellingAnalysis {
  keyPoints: Array<{
    originalPoint: string;
    coverage: 'full' | 'partial' | 'missing';
    userExpression?: string;
    feedback?: string;
  }>;
  coverageScore: number;
  suggestedVocab: Array<{
    word: string;
    userAlternative?: string;
    context: string;
    suggestion: string;
  }>;
  hesitations?: Array<{
    position: string;
    transcript: string;
  }>;
  bandEstimate?: number;
  overallFeedback: string;
}

/** Retelling 记录 */
export interface RetellingRecord {
  id: string;
  recordingDataUrl: string;
  transcript: string;
  analysis: RetellingAnalysis;
  createdAt: number;
}

/** 影子跟读素材 */
export interface ShadowMaterial {
  id: string;
  title: string;
  videoBlobId?: string;
  subtitles: SubtitleSegment[];
  durationMs: number;
  progress: ShadowProgress;
  retellingHistory: RetellingRecord[];
  createdAt: number;
  updatedAt: number;
}

/** 视频 Blob 存储 */
export interface VideoBlob {
  id: string;
  blob: Blob;
  mimeType: string;
  size: number;
  createdAt: number;
}

/** AI 对话轮次 */
export interface DialogTurn {
  role: 'ai' | 'user';
  content: string;
  recordingDataUrl?: string;
  feedback?: {
    score: number;
    suggestions: string[];
  };
  timestamp: number;
}

/** AI 对话会话 */
export interface DialogSession {
  id: string;
  materialId?: string;
  topic: string;
  targetBand: number;
  turns: DialogTurn[];
  createdAt: number;
  updatedAt: number;
}

// ============= 数据库定义 =============

const DEFAULT_DB_NAME = 'shadow-reading-db';

export class ShadowReadingDatabase extends Dexie {
  materials!: Table<ShadowMaterial, string>;
  blobs!: Table<VideoBlob, string>;
  dialogSessions!: Table<DialogSession, string>;

  constructor(name: string = DEFAULT_DB_NAME) {
    super(name);

    this.version(1).stores({
      materials: '&id, title, createdAt, updatedAt',
      blobs: '&id, createdAt',
      dialogSessions: '&id, materialId, createdAt, updatedAt',
    });
  }
}

export const shadowDb = new ShadowReadingDatabase();

// ============= CRUD 操作 =============

const nowTs = () => Date.now();

/** 创建素材 */
export async function createMaterial(
  material: Omit<ShadowMaterial, 'id' | 'createdAt' | 'updatedAt'> & { id?: string },
  db: ShadowReadingDatabase = shadowDb
): Promise<string> {
  const now = nowTs();
  const id = material.id ?? `shadow-${now}-${Math.random().toString(36).slice(2, 8)}`;
  const record: ShadowMaterial = {
    ...material,
    id,
    progress: material.progress ?? { practicedSentences: [], fluencyScores: {} },
    retellingHistory: material.retellingHistory ?? [],
    createdAt: now,
    updatedAt: now,
  };
  await db.materials.add(record);
  return id;
}

/** 获取素材 */
export async function getMaterial(
  id: string,
  db: ShadowReadingDatabase = shadowDb
): Promise<ShadowMaterial | undefined> {
  return db.materials.get(id);
}

/** 获取所有素材 */
export async function getAllMaterials(
  db: ShadowReadingDatabase = shadowDb
): Promise<ShadowMaterial[]> {
  return db.materials.orderBy('updatedAt').reverse().toArray();
}

/** 更新素材 */
export async function updateMaterial(
  id: string,
  updates: Partial<Omit<ShadowMaterial, 'id' | 'createdAt'>>,
  db: ShadowReadingDatabase = shadowDb
): Promise<ShadowMaterial> {
  const existing = await db.materials.get(id);
  if (!existing) {
    throw new Error(`Material ${id} not found`);
  }

  const updatedAt = nowTs();
  const payload: ShadowMaterial = {
    ...existing,
    ...updates,
    updatedAt,
  };

  await db.materials.put(payload);
  return payload;
}

/** 删除素材（包括关联的视频 Blob） */
export async function deleteMaterial(
  id: string,
  db: ShadowReadingDatabase = shadowDb
): Promise<void> {
  const material = await db.materials.get(id);
  if (material?.videoBlobId) {
    await db.blobs.delete(material.videoBlobId);
  }
  await db.materials.delete(id);
}

/** 存储视频 Blob */
export async function storeVideoBlob(
  blob: Blob,
  db: ShadowReadingDatabase = shadowDb
): Promise<string> {
  const now = nowTs();
  const id = `video-${now}-${Math.random().toString(36).slice(2, 8)}`;
  const record: VideoBlob = {
    id,
    blob,
    mimeType: blob.type,
    size: blob.size,
    createdAt: now,
  };
  await db.blobs.add(record);
  return id;
}

/** 获取视频 Blob */
export async function getVideoBlob(
  id: string,
  db: ShadowReadingDatabase = shadowDb
): Promise<VideoBlob | undefined> {
  return db.blobs.get(id);
}

/** 更新素材进度 */
export async function updateProgress(
  materialId: string,
  sentenceIndex: number,
  fluencyScore?: number,
  db: ShadowReadingDatabase = shadowDb
): Promise<void> {
  const material = await db.materials.get(materialId);
  if (!material) return;

  const progress = { ...material.progress };

  if (!progress.practicedSentences.includes(sentenceIndex)) {
    progress.practicedSentences = [...progress.practicedSentences, sentenceIndex];
  }

  if (fluencyScore !== undefined) {
    progress.fluencyScores = { ...progress.fluencyScores, [sentenceIndex]: fluencyScore };
  }

  progress.lastPracticedAt = nowTs();

  // 计算整体流利度
  const scores = Object.values(progress.fluencyScores);
  if (scores.length > 0) {
    progress.overallFluency = scores.reduce((a, b) => a + b, 0) / scores.length;
  }

  await updateMaterial(materialId, { progress }, db);
}

/** 添加 Retelling 记录 */
export async function addRetellingRecord(
  materialId: string,
  record: Omit<RetellingRecord, 'id' | 'createdAt'>,
  db: ShadowReadingDatabase = shadowDb
): Promise<string> {
  const material = await db.materials.get(materialId);
  if (!material) throw new Error(`Material ${materialId} not found`);

  const now = nowTs();
  const id = `retelling-${now}-${Math.random().toString(36).slice(2, 8)}`;
  const newRecord: RetellingRecord = {
    ...record,
    id,
    createdAt: now,
  };

  await updateMaterial(materialId, {
    retellingHistory: [newRecord, ...material.retellingHistory].slice(0, 10),
  }, db);

  return id;
}

// ============= AI 对话操作 =============

/** 创建对话会话 */
export async function createDialogSession(
  session: Omit<DialogSession, 'id' | 'createdAt' | 'updatedAt' | 'turns'> & { id?: string },
  db: ShadowReadingDatabase = shadowDb
): Promise<string> {
  const now = nowTs();
  const id = session.id ?? `dialog-${now}-${Math.random().toString(36).slice(2, 8)}`;
  const record: DialogSession = {
    ...session,
    id,
    turns: [],
    createdAt: now,
    updatedAt: now,
  };
  await db.dialogSessions.add(record);
  return id;
}

/** 获取对话会话 */
export async function getDialogSession(
  id: string,
  db: ShadowReadingDatabase = shadowDb
): Promise<DialogSession | undefined> {
  return db.dialogSessions.get(id);
}

/** 添加对话轮次 */
export async function addDialogTurn(
  sessionId: string,
  turn: Omit<DialogTurn, 'timestamp'>,
  db: ShadowReadingDatabase = shadowDb
): Promise<void> {
  const session = await db.dialogSessions.get(sessionId);
  if (!session) throw new Error(`Session ${sessionId} not found`);

  const now = nowTs();
  const newTurn: DialogTurn = { ...turn, timestamp: now };

  await db.dialogSessions.put({
    ...session,
    turns: [...session.turns, newTurn],
    updatedAt: now,
  });
}

/** 获取素材的对话会话列表 */
export async function getDialogSessionsByMaterial(
  materialId: string,
  db: ShadowReadingDatabase = shadowDb
): Promise<DialogSession[]> {
  return db.dialogSessions
    .where('materialId')
    .equals(materialId)
    .reverse()
    .sortBy('updatedAt');
}

/** 创建隔离的数据库实例（用于测试） */
export function createIsolatedShadowDatabase(name?: string): ShadowReadingDatabase {
  return new ShadowReadingDatabase(name);
}
